/**
 * Structure-of-arrays entity stores.
 *
 * Everything hot lives in typed arrays: no per-entity object allocation, no GC
 * churn during a 400-unit brawl, and a state hash that is a straight linear
 * scan. Entities are addressed by a packed handle (`index | gen << 16`) so a
 * stale reference to a dead unit can be detected instead of silently aliasing
 * whoever was spawned into that slot next.
 */

import { MAX_BUILDINGS, MAX_PROJECTILES, MAX_UNITS } from './constants.ts';

export const NULL_HANDLE = -1;

export function packHandle(index: number, gen: number): number {
  return (index & 0xffff) | ((gen & 0x7fff) << 16);
}

export function handleIndex(h: number): number {
  return h & 0xffff;
}

export function handleGen(h: number): number {
  return (h >>> 16) & 0x7fff;
}

/**
 * A dense free-list allocator shared by every store.
 *
 * Exported so `ResourceNodeStore` in `systems/nodes.ts` can be built on the same
 * one — which is what makes `saveStore`/`loadStore` work on it unmodified, since
 * they only ever ask a store for `slots` and a list of columns.
 */
export class SlotAllocator {
  readonly capacity: number;
  readonly alive: Uint8Array;
  readonly gen: Uint16Array;
  /** Dense list of live indices, kept sorted ascending so iteration is stable. */
  private free: number[] = [];
  count = 0;
  /** Highest index ever allocated + 1. Iterate [0, high) to visit all slots. */
  high = 0;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.alive = new Uint8Array(capacity);
    this.gen = new Uint16Array(capacity);
  }

  alloc(): number {
    let idx: number;
    if (this.free.length > 0) {
      // Pop the *lowest* free slot so allocation order is a pure function of
      // history, not of the order things happened to die.
      idx = this.free.pop() as number;
    } else {
      if (this.high >= this.capacity) return -1;
      idx = this.high++;
    }
    this.alive[idx] = 1;
    this.count++;
    return idx;
  }

  release(idx: number): void {
    if (this.alive[idx] === 0) return;
    this.alive[idx] = 0;
    this.gen[idx] = (this.gen[idx] + 1) & 0x7fff;
    this.count--;
    this.free.push(idx);
    // Keep the free list descending so `pop()` yields the smallest index.
    for (let i = this.free.length - 1; i > 0; i--) {
      if (this.free[i] > this.free[i - 1]) {
        const t = this.free[i];
        this.free[i] = this.free[i - 1];
        this.free[i - 1] = t;
      } else break;
    }
  }

  reset(): void {
    this.alive.fill(0);
    this.gen.fill(0);
    this.free.length = 0;
    this.count = 0;
    this.high = 0;
  }

  freeListSnapshot(): number[] {
    return this.free.slice();
  }

  restoreFreeList(list: number[]): void {
    this.free = list.slice();
  }

  /**
   * Save/load support. `high`, `count` and the free list together define which
   * slots exist and in what order the next allocation will reuse them — get any
   * of the three wrong and a loaded game diverges from the saved one.
   */
  save(): { high: number; count: number; free: number[]; alive: number[]; gen: number[] } {
    const alive: number[] = [];
    const gen: number[] = [];
    for (let i = 0; i < this.high; i++) {
      alive.push(this.alive[i]);
      gen.push(this.gen[i]);
    }
    return { high: this.high, count: this.count, free: this.free.slice(), alive, gen };
  }

  load(s: { high: number; count: number; free: number[]; alive: number[]; gen: number[] }): void {
    this.reset();
    this.high = s.high;
    this.count = s.count;
    this.free = s.free.slice();
    for (let i = 0; i < s.high; i++) {
      this.alive[i] = s.alive[i];
      this.gen[i] = s.gen[i];
    }
  }
}

export class UnitStore {
  readonly slots = new SlotAllocator(MAX_UNITS);

  readonly typeId = new Uint16Array(MAX_UNITS);
  readonly team = new Uint8Array(MAX_UNITS);
  readonly faction = new Uint8Array(MAX_UNITS);

  readonly x = new Float32Array(MAX_UNITS);
  readonly y = new Float32Array(MAX_UNITS);
  readonly vx = new Float32Array(MAX_UNITS);
  readonly vy = new Float32Array(MAX_UNITS);
  readonly facing = new Float32Array(MAX_UNITS);

  readonly hp = new Float32Array(MAX_UNITS);
  readonly maxHp = new Float32Array(MAX_UNITS);
  readonly morale = new Float32Array(MAX_UNITS);

  readonly state = new Uint8Array(MAX_UNITS);
  readonly stateT = new Float32Array(MAX_UNITS);
  readonly attackCd = new Float32Array(MAX_UNITS);
  /** 0..1 progress through the current attack animation (windup → contact → recovery). */
  readonly swing = new Float32Array(MAX_UNITS);
  readonly flags = new Uint16Array(MAX_UNITS);

  /**
   * Company id, not company index — and `Int32Array` rather than `Int16Array`
   * because ids come from `nextCompanyId`, which only ever counts up. A long
   * grinder run raising and losing companies at city scale passes 32767 in a
   * few hours, and the old width wrapped it negative, which reads throughout
   * the simulation as "this soldier is in no company at all".
   */
  readonly companyId = new Int32Array(MAX_UNITS);
  /** Index within the company, drives formation slot assignment. */
  readonly slotIdx = new Int16Array(MAX_UNITS);
  readonly targetH = new Int32Array(MAX_UNITS);
  readonly targetBuilding = new Int16Array(MAX_UNITS);
  /** Tile index of a wall segment this unit is hacking at, or -1. */
  readonly targetWall = new Int32Array(MAX_UNITS);
  /**
   * The section of curtain this model has been posted to, or −1.
   *
   * The whole of how a garrison is represented. It is the model's *assignment*,
   * not its position: a man walking the last two tiles to his post carries it
   * and is still on the ground, and `standingOn` in `systems/garrison.ts` is
   * what turns it into "and he is up there now". It is state — it decides where
   * he steers, what he can reach, what can reach him, and whether the collision
   * pass lets him onto stone — so it is hashed and saved with the rest.
   */
  readonly wallTile = new Int32Array(MAX_UNITS);
  /** Walkway leg: -1 ground, tile while walking, -tile-2 while descending. */
  readonly wallAt = new Int32Array(MAX_UNITS);

  /** Destination the unit is steering toward (formation slot or order point). */
  readonly goalX = new Float32Array(MAX_UNITS);
  readonly goalY = new Float32Array(MAX_UNITS);

  readonly radius = new Float32Array(MAX_UNITS);
  readonly speed = new Float32Array(MAX_UNITS);
  readonly job = new Uint8Array(MAX_UNITS);
  readonly workSite = new Int16Array(MAX_UNITS);

  /**
   * What is on this worker's back, and what kind of thing it is.
   *
   * The brief's "a worker carrying materials **back**", and it is state rather
   * than decoration for the ordinary reason: a man two strides from the
   * storehouse with three timber on his shoulder and a man who has just set out
   * empty are two different worlds, and they diverge the moment either of them
   * arrives. Hashed and saved with the rest.
   *
   * `carryKind` is `RESOURCE_KEYS` index **plus one**, so **0 means an empty
   * pair of hands** rather than "carrying gold". A `Uint8Array` because there
   * are twenty-four resources and there will never be two hundred and fifty.
   */
  readonly carry = new Float32Array(MAX_UNITS);
  readonly carryKind = new Uint8Array(MAX_UNITS);
  /**
   * The resource node this worker is felling, or −1.
   *
   * **The typed-array trap applies to this one**, exactly as it did to
   * `wallTile`: `loadStore` copies straight into an `Int32Array`, so an absent
   * column writes `undefined`, which lands as **0** — node slot zero, a
   * perfectly real tree somewhere on the map that every worker in an old save
   * would set off toward at once. `migrate` fills it with −1 explicitly and
   * there is a test.
   */
  readonly nodeTarget = new Int32Array(MAX_UNITS);
  /**
   * Seconds of patience left on the walk to that node.
   *
   * Set when a node is claimed, spent while walking, refilled on arrival. When
   * it runs out the worker gives the node up — see `HAUL_PATIENCE` for the
   * hazard this exists to make survivable.
   */
  readonly haulT = new Float32Array(MAX_UNITS);

  /** Status-effect timers, in seconds remaining. */
  readonly slowT = new Float32Array(MAX_UNITS);
  readonly poisonT = new Float32Array(MAX_UNITS);
  readonly fearT = new Float32Array(MAX_UNITS);
  readonly stunT = new Float32Array(MAX_UNITS);
  readonly rallyT = new Float32Array(MAX_UNITS);
  readonly healT = new Float32Array(MAX_UNITS);
  readonly madnessT = new Float32Array(MAX_UNITS);
  readonly shieldT = new Float32Array(MAX_UNITS);
  readonly lifeT = new Float32Array(MAX_UNITS);

  readonly knockX = new Float32Array(MAX_UNITS);
  readonly knockY = new Float32Array(MAX_UNITS);
  readonly lastHitTick = new Int32Array(MAX_UNITS);
  readonly heroId = new Int8Array(MAX_UNITS);

  /**
   * Missile troops carry a finite quiver. When it runs dry they are infantry
   * with a knife, which is the whole reason a missile line has to be protected
   * and the whole reason a cavalry flank is worth making.
   */
  readonly ammo = new Float32Array(MAX_UNITS);
  /**
   * 0..1. Running and swinging build it; standing still sheds it. Tired troops
   * hit softer and move slower — it is what makes a reserve worth holding and
   * what makes chasing routers across the field a real cost.
   */
  readonly fatigue = new Float32Array(MAX_UNITS);

  spawn(): number {
    const i = this.slots.alloc();
    if (i < 0) return -1;
    this.typeId[i] = 0;
    this.team[i] = 0;
    this.faction[i] = 0;
    this.x[i] = 0;
    this.y[i] = 0;
    this.vx[i] = 0;
    this.vy[i] = 0;
    this.facing[i] = 0;
    this.hp[i] = 1;
    this.maxHp[i] = 1;
    this.morale[i] = 1;
    this.state[i] = 0;
    this.stateT[i] = 0;
    this.attackCd[i] = 0;
    this.swing[i] = 0;
    this.flags[i] = 0;
    this.companyId[i] = -1;
    this.slotIdx[i] = -1;
    this.targetH[i] = NULL_HANDLE;
    this.targetBuilding[i] = -1;
    this.targetWall[i] = -1;
    this.wallTile[i] = -1;
    this.wallAt[i] = -1;
    this.goalX[i] = 0;
    this.goalY[i] = 0;
    this.radius[i] = 0.28;
    this.speed[i] = 1.5;
    this.job[i] = 0;
    this.workSite[i] = -1;
    this.carry[i] = 0;
    this.carryKind[i] = 0;
    this.nodeTarget[i] = -1;
    this.haulT[i] = 0;
    this.slowT[i] = 0;
    this.poisonT[i] = 0;
    this.fearT[i] = 0;
    this.stunT[i] = 0;
    this.rallyT[i] = 0;
    this.healT[i] = 0;
    this.madnessT[i] = 0;
    this.shieldT[i] = 0;
    this.lifeT[i] = 0;
    this.knockX[i] = 0;
    this.knockY[i] = 0;
    this.lastHitTick[i] = -1000;
    this.heroId[i] = -1;
    this.ammo[i] = 0;
    this.fatigue[i] = 0;
    return i;
  }

  kill(i: number): void {
    this.slots.release(i);
  }

  isAlive(i: number): boolean {
    return this.slots.alive[i] === 1;
  }

  handle(i: number): number {
    return packHandle(i, this.slots.gen[i]);
  }

  /** Resolve a handle to an index, or -1 if the referent is gone. */
  resolve(h: number): number {
    if (h < 0) return -1;
    const i = handleIndex(h);
    if (i >= this.slots.capacity) return -1;
    if (this.slots.alive[i] === 0) return -1;
    if (this.slots.gen[i] !== handleGen(h)) return -1;
    return i;
  }

  reset(): void {
    this.slots.reset();
  }
}

export class ProjectileStore {
  readonly slots = new SlotAllocator(MAX_PROJECTILES);

  readonly x = new Float32Array(MAX_PROJECTILES);
  readonly y = new Float32Array(MAX_PROJECTILES);
  /** Height above ground; arrows arc, bolts do not. */
  readonly z = new Float32Array(MAX_PROJECTILES);
  readonly vx = new Float32Array(MAX_PROJECTILES);
  readonly vy = new Float32Array(MAX_PROJECTILES);
  readonly vz = new Float32Array(MAX_PROJECTILES);
  readonly ttl = new Float32Array(MAX_PROJECTILES);
  readonly damage = new Float32Array(MAX_PROJECTILES);
  readonly dmgType = new Uint8Array(MAX_PROJECTILES);
  readonly team = new Uint8Array(MAX_PROJECTILES);
  readonly kind = new Uint8Array(MAX_PROJECTILES);
  readonly splash = new Float32Array(MAX_PROJECTILES);
  readonly targetH = new Int32Array(MAX_PROJECTILES);
  readonly ownerH = new Int32Array(MAX_PROJECTILES);
  readonly tx = new Float32Array(MAX_PROJECTILES);
  readonly ty = new Float32Array(MAX_PROJECTILES);

  spawn(): number {
    const i = this.slots.alloc();
    if (i < 0) return -1;
    this.z[i] = 0;
    this.vz[i] = 0;
    this.splash[i] = 0;
    this.targetH[i] = NULL_HANDLE;
    this.ownerH[i] = NULL_HANDLE;
    return i;
  }

  kill(i: number): void {
    this.slots.release(i);
  }

  reset(): void {
    this.slots.reset();
  }
}

export class BuildingStore {
  readonly slots = new SlotAllocator(MAX_BUILDINGS);

  readonly typeId = new Uint16Array(MAX_BUILDINGS);
  /**
   * Tile coordinates, and they must outlive the map they index.
   *
   * These were `Uint8Array`, which was exactly right while the map was 128
   * tiles a side and silently wrong the moment it became 384: a building placed
   * past tile 255 wrapped to the far side of the world. Nothing in a normal run
   * reaches it — the settlement grows outward from a Hearth at tile 192 — so it
   * would have waited for the first player to build toward the eastern edge and
   * then presented as a building that renders in one place and is attacked in
   * another.
   *
   * `Uint16Array` covers any map this engine can address: `packHandle` masks
   * slot indices to sixteen bits, so 65,536 is already the ceiling elsewhere.
   */
  readonly tx = new Uint16Array(MAX_BUILDINGS);
  readonly ty = new Uint16Array(MAX_BUILDINGS);
  readonly hp = new Float32Array(MAX_BUILDINGS);
  readonly maxHp = new Float32Array(MAX_BUILDINGS);
  readonly team = new Uint8Array(MAX_BUILDINGS);
  /** 0..1 construction progress; 1 = finished and working. */
  readonly build = new Float32Array(MAX_BUILDINGS);
  readonly workers = new Uint8Array(MAX_BUILDINGS);
  readonly tier = new Uint8Array(MAX_BUILDINGS);
  readonly cooldown = new Float32Array(MAX_BUILDINGS);
  readonly stock = new Float32Array(MAX_BUILDINGS);
  readonly variant = new Uint8Array(MAX_BUILDINGS);
  readonly lastHitTick = new Int32Array(MAX_BUILDINGS);
  readonly targetH = new Int32Array(MAX_BUILDINGS);
  /**
   * The share of this site's materials that had to be carted in from another of
   * the crown's towns, 0..1 — see `campaign/commonwealth.ts`.
   *
   * On the *site* rather than on the world, because it is a fact about one
   * foundation: the hall you paid for out of your own yards goes up at full
   * speed while the one beside it, built on grain from three provinces away,
   * crawls. Read once per tick per site in `doSiteWork` and never written again
   * after `doBuild`.
   */
  readonly haul = new Float32Array(MAX_BUILDINGS);

  spawn(): number {
    const i = this.slots.alloc();
    if (i < 0) return -1;
    this.build[i] = 0;
    this.workers[i] = 0;
    this.tier[i] = 1;
    this.cooldown[i] = 0;
    this.stock[i] = 0;
    this.variant[i] = 0;
    this.lastHitTick[i] = -1000;
    this.targetH[i] = NULL_HANDLE;
    this.haul[i] = 0;
    return i;
  }

  kill(i: number): void {
    this.slots.release(i);
  }

  isAlive(i: number): boolean {
    return this.slots.alive[i] === 1;
  }

  reset(): void {
    this.slots.reset();
  }
}
